import { h as __h } from "preact";
import { signal, untracked } from "@preact/signals";
/** @import { Signal } from "@preact/signals"; */
import { Component, createRef } from "preact";
/** @import { ComponentChildren, RefObject } from "preact"; */

/** @import { Placement } from "~/util/float.js"; */
import html from "~/util/html.js";
import Popover from "~/component/Popover.js";

/**
 * @typedef {object} PopoverEntry
 * @property {string} id
 * @property {HTMLElement} anchor
 * @property {Placement} placement
 * @property {(resolve: (result?: any) => void) => ComponentChildren} content
 * @property {(result?: any) => void} resolve
 */

/** @type {Signal<PopoverEntry[]>} */
const popovers = signal([]);

/**
 * @template [T=void]
 * @param {object} options
 * @param {HTMLElement} options.anchor
 * @param {Placement} options.placement
 * @param {(resolve: (result?: T) => void) => ComponentChildren} options.content
 * @returns {Promise<T | undefined>} Settles with the resolved result, or undefined on dismissal
 */
export function popover({
  anchor,
  placement,
  content
}) {
  const id = crypto.randomUUID();

  /** @type {PromiseWithResolvers<T | undefined>} */
  const {
    promise,
    resolve: settle
  } = Promise.withResolvers();

  /** @param {T} [result] */
  const resolve = result => {
    popovers.value = untracked(() => popovers.value).filter(popover => popover.id !== id);
    settle(result);
  };
  popovers.value = untracked(() => popovers.value).concat({
    id,
    anchor,
    placement,
    content,
    resolve
  });
  return promise;
}
export function PopoverProvider() {
  return popovers.value.map(entry => __h(SpawnedPopover, {
    key: entry.id,
    entry: entry
  }));
}

/**
 * @extends {Component<SpawnedProps>}
 *
 * @typedef {object} SpawnedProps
 * @property {PopoverEntry} entry
 */
class SpawnedPopover extends Component {
  /** @type {RefObject<Popover>} */
  #popover = createRef();

  /** @override */
  componentDidMount() {
    this.#popover.current?.setReference(this.props.entry.anchor);
    this.#popover.current?.show();
  }
  #onClose = () => {
    const {
      anchor,
      resolve
    } = this.props.entry;
    const active = document.activeElement;
    if (anchor.isConnected && (active === null || active === document.body)) anchor.focus();
    resolve();
  };
  render() {
    const {
      entry
    } = this.props;
    return __h(Popover, {
      ref: this.#popover,
      id: entry.id,
      anchor: entry.anchor,
      placement: entry.placement,
      onClose: this.#onClose
    }, entry.content(entry.resolve));
  }
}